/**
 * Centralized funnel analytics utility.
 * Initializes GA4 + Meta Pixel on first call (idempotent), exposes a single track() entry point.
 * Driven by env vars REACT_APP_GA4_ID and REACT_APP_FB_PIXEL_ID — silent no-op if either is unset.
 *
 * Mobile-safe: all script injection is async/defer.
 * Page-safe: a single global init flag prevents duplicate firing across SPA route changes.
 */

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || "";
const DEBUG = process.env.REACT_APP_ANALYTICS_DEBUG === "true";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

let initialized = false;

const log = (...args) => {
  if (DEBUG) console.log("[analytics]", ...args);
};

const initGA4 = () => {
  if (!GA4_ID || typeof window === "undefined") return;
  if (window.gtag) return;
  const s = document.createElement("script");
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  s.async = true;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, { send_page_view: true });
  log("GA4 initialized", GA4_ID);
};

const initMetaPixel = () => {
  if (!FB_PIXEL_ID || typeof window === "undefined") return;
  if (window.fbq) return;
  // Official Meta Pixel base snippet (rewritten as ES module-safe code)
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", FB_PIXEL_ID);
  window.fbq("track", "PageView");
  log("Meta Pixel initialized", FB_PIXEL_ID);
};

export const initAnalytics = () => {
  if (initialized || typeof window === "undefined") return;
  initialized = true;
  initGA4();
  initMetaPixel();
  // Always provision dataLayer so downstream tag managers can read it
  window.dataLayer = window.dataLayer || [];
};

/**
 * Fire a structured funnel event. Pushes to:
 *   1. window.dataLayer (always — for GTM / debugging)
 *   2. GA4 via gtag() (if configured)
 *   3. Meta Pixel via fbq() — only for known conversion events
 *
 * @param {string} eventName  snake_case event ID, e.g. "checklist_form_completed"
 * @param {object} params     optional payload (camelCase keys, kept compact)
 */
export const track = (eventName, params = {}) => {
  if (!eventName) return;
  if (typeof window === "undefined") return;
  if (!initialized) initAnalytics();

  const payload = { event: eventName, ...params, ts: Date.now() };

  // 1. dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);

  // 2. GA4
  if (window.gtag) {
    window.gtag("event", eventName, params);
  }

  // 3. Meta Pixel — map known conversion events
  if (window.fbq) {
    const fbMap = {
      checklist_form_completed: ["Lead", { content_name: "Renter Checklist" }],
      pdf_download_clicked: ["ViewContent", { content_name: params.pdf_id || "pdf" }],
      lead_created: ["Lead", { content_name: params.source || "lead" }],
      quiz_completed: ["CompleteRegistration", { content_name: "Match Quiz" }],
    };
    const fbEntry = fbMap[eventName];
    if (fbEntry) window.fbq("track", fbEntry[0], fbEntry[1]);
  }

  // 4. Backend sink (fire-and-forget, never blocks) — pulls UTM from attribution sessionStorage
  if (BACKEND_URL) {
    try {
      let attr = {};
      try { attr = JSON.parse(sessionStorage.getItem("unq_attribution_v1") || "{}"); } catch {}
      const body = {
        event: eventName,
        email: params.email || undefined,
        pdf_id: params.pdf_id || undefined,
        source: params.source || undefined,
        utm_source: attr.utm_source || undefined,
        utm_medium: attr.utm_medium || undefined,
        utm_campaign: attr.utm_campaign || undefined,
        extra: { ...params, ts: payload.ts },
      };
      // Use Beacon API where possible — survives page unload (e.g. PDF download_clicked navigates away)
      if (navigator.sendBeacon) {
        const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
        navigator.sendBeacon(`${BACKEND_URL}/api/track/event`, blob);
      } else {
        fetch(`${BACKEND_URL}/api/track/event`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
          keepalive: true,
        }).catch(() => {});
      }
    } catch (e) {
      log("backend sink failed", e);
    }
  }

  log(eventName, params);
};
